import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import {
  DeviceDateRangeScheduleDto,
  DeviceDayScheduleDto,
  DeviceGroupService,
  DeviceService,
} from '@api-client';
import dayjs from 'dayjs';
import {
  Button,
  Hint,
  InputDatePicker,
  InputTime,
  Label,
  Select,
  Toggle,
} from '../../components';
import { ContentControllerStore } from '../content-controller.store';
import { injectClientStore } from '../../shared/device-schedule/client-store-injector';
import { ToastService as ToastServiceType } from '../../../services/toast.service';

type ScheduleType = 'daterange' | 'day';

@Component({
  selector: 'app-schedule',
  standalone: true,
  imports: [
    CommonModule,
    Hint,
    Toggle,
    Select,
    Label,
    InputTime,
    Button,
    InputDatePicker,
  ],
  templateUrl: './schedule.page.html',
  styleUrls: ['./schedule.page.scss'],
})
export class SchedulePage {
  private readonly store = inject(ContentControllerStore);
  private readonly deviceService = inject(DeviceService);
  private readonly deviceGroupService = inject(DeviceGroupService);
  private readonly toastService = inject(ToastServiceType);

  private clientStore = injectClientStore();
  client = this.clientStore.client;

  device = computed(() => this.store.deviceResource.value());
  isGroupMode = computed(() => !!this.store.groupId());
  groupId = computed(() => this.store.groupId());

  scheduleEnabled = signal<boolean>(false);
  timeActive = signal<boolean>(false);

  scheduleType = signal<ScheduleType>('daterange');

  startDate = signal<Date>(new Date());
  endDate = signal<Date>(dayjs().endOf('day').toDate());
  seletedDays = signal<number[]>([]);

  startTime = signal<string>('00:00');
  endTime = signal<string>('00:00');

  validTimeRange = signal<boolean>(true);

  selectOptions = [
    { label: '날짜', value: 'daterange' },
    { label: '요일', value: 'day' },
  ];

  dayItems = [
    { label: '일', value: 0 },
    { label: '월', value: 1 },
    { label: '화', value: 2 },
    { label: '수', value: 3 },
    { label: '목', value: 4 },
    { label: '금', value: 5 },
    { label: '토', value: 6 },
  ];

  private lastScheduleSignature = signal<string>('');

  constructor() {
    effect(() => {
      const device = this.device();
      if (!device?.schedule) return;

      const signature = JSON.stringify(device.schedule);
      if (this.lastScheduleSignature() === signature) return;

      this.lastScheduleSignature.set(signature);
      this.initializeFromSchedule(device.schedule);
    });
  }

  private initializeFromSchedule(
    schedule: DeviceDateRangeScheduleDto | DeviceDayScheduleDto,
  ) {
    this.scheduleEnabled.set(schedule.enabled);
    this.startTime.set(schedule.startTime ?? '00:00');
    this.endTime.set(schedule.endTime ?? '00:00');
    this.scheduleType.set(schedule.type as ScheduleType);
    this.timeActive.set(!!schedule.startTime && !!schedule.endTime);

    if (schedule.type === 'daterange') {
      const _schedule = schedule as DeviceDateRangeScheduleDto;
      this.startDate.set(dayjs(_schedule.startsAt).toDate());
      this.endDate.set(dayjs(_schedule.endsAt).toDate());
    } else {
      const _schedule = schedule as DeviceDayScheduleDto;
      this.seletedDays.set(_schedule.enabledDays);
    }
  }

  seleteDay(day: number) {
    this.seletedDays.update((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      }
      return [...prev, day];
    });
  }

  validateTimeRange(startTime: string, endTime: string): boolean {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
      return false;
    }

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    if (startMinutes >= endMinutes) {
      return false;
    }

    return true;
  }

  submit() {
    const deviceId = this.device()?.id;
    const client = this.client();
    const groupId = this.groupId();

    if (!client) return;
    if (!this.isGroupMode() && !deviceId) return;
    if (this.isGroupMode() && !groupId) return;

    if (
      this.timeActive() &&
      !this.validateTimeRange(this.startTime(), this.endTime())
    ) {
      this.toastService.error('시작 시간은 종료 시간보다 빨라야 합니다.');
      return;
    }

    const scheduleBody =
      this.scheduleType() === 'daterange'
        ? {
            enabled: this.scheduleEnabled(),
            type: 'daterange' as const,
            startsAt: dayjs(this.startDate()).startOf('day').toISOString(),
            endsAt: dayjs(this.endDate()).endOf('day').toISOString(),
            startTime: this.timeActive() ? this.startTime() : null,
            endTime: this.timeActive() ? this.endTime() : null,
          }
        : {
            enabled: this.scheduleEnabled(),
            type: 'day' as const,
            enabledDays: this.seletedDays(),
            startTime: this.timeActive() ? this.startTime() : null,
            endTime: this.timeActive() ? this.endTime() : null,
          };

    if (this.isGroupMode() && groupId) {
      this.deviceGroupService
        .deviceGroupControllerApplyContent({
          id: groupId,
          clientWorkspaceId: client.id,
          body: {
            schedule: scheduleBody as unknown as Record<string, unknown>,
          },
        })
        .subscribe({
          next: () => {
            this.toastService.success(
              '그룹 전체 기기에 스케줄이 설정되었습니다.',
            );
            this.store.deviceResource.reload();
          },
          error: (err: { error?: { message?: string } }) => {
            console.error('그룹 스케줄 설정 실패:', err);
            this.toastService.error('그룹 스케줄 설정에 실패했습니다.');
          },
        });
      return;
    }

    this.deviceService
      .deviceControllerSetSchedule({
        deviceId: deviceId!,
        clientWorkspaceId: client.id,
        body: scheduleBody,
      })
      .subscribe({
        next: () => {
          this.toastService.success('스케줄이 설정되었습니다.');
          this.store.deviceResource.reload();
        },
        error: (err: { error?: { message?: string } }) => {
          console.error('스케줄 설정 실패:', err);
          this.toastService.error('스케줄 설정에 실패했습니다.');
        },
      });
  }
}
